import {
  COSMETIC_IDS,
  cloneProgress,
  validateProgress,
  type ProgressSettings,
  type Progress,
} from "./schema";

export type ExplicitProgressSource = "local" | "cloud";

const settingsEqual = (
  first: ProgressSettings,
  second: ProgressSettings,
): boolean =>
  first.music === second.music &&
  first.sfx === second.sfx &&
  first.vibration === second.vibration &&
  first.reducedMotion === second.reducedMotion &&
  first.reducedParticles === second.reducedParticles &&
  first.performance === second.performance &&
  first.theme === second.theme &&
  first.skipReadyCountdown === second.skipReadyCountdown;

export const officialProgressEqual = (
  first: Progress,
  second: Progress,
): boolean =>
  (Object.keys(first.records) as Array<keyof Progress["records"]>).every(
    (key) => first.records[key] === second.records[key],
  ) &&
  first.tutorial.completed === second.tutorial.completed &&
  first.tutorial.step === second.tutorial.step &&
  first.currency === second.currency &&
  COSMETIC_IDS.every(
    (id) =>
      Boolean(first.ownedCosmetics[id]) === Boolean(second.ownedCosmetics[id]),
  ) &&
  first.equippedCosmetic === second.equippedCosmetic;

export const progressNeedsExplicitSource = (
  local: Progress,
  cloud: Progress,
): boolean =>
  !settingsEqual(local.settings, cloud.settings) ||
  local.equippedCosmetic !== cloud.equippedCosmetic;

export const progressContentEqual = (
  first: Progress,
  second: Progress,
): boolean =>
  (Object.keys(first.records) as Array<keyof Progress["records"]>).every(
    (key) => first.records[key] === second.records[key],
  ) &&
  settingsEqual(first.settings, second.settings) &&
  first.tutorial.completed === second.tutorial.completed &&
  first.tutorial.step === second.tutorial.step &&
  first.currency === second.currency &&
  COSMETIC_IDS.every(
    (id) =>
      Boolean(first.ownedCosmetics[id]) === Boolean(second.ownedCosmetics[id]),
  ) &&
  first.equippedCosmetic === second.equippedCosmetic;

export const progressIsMonotonicFrom = (
  previous: Progress,
  next: Progress,
): boolean => {
  for (const key of Object.keys(previous.records) as Array<
    keyof Progress["records"]
  >) {
    if (next.records[key] < previous.records[key]) return false;
  }
  if (
    next.currency < previous.currency ||
    next.tutorial.step < previous.tutorial.step
  ) {
    return false;
  }
  return Object.keys(previous.ownedCosmetics).every(
    (id) =>
      next.ownedCosmetics[id as keyof Progress["ownedCosmetics"]] === true,
  );
};

export const mergeProgress = (
  local: Progress,
  cloud: Progress,
  source: ExplicitProgressSource,
  now = Date.now(),
): Progress => {
  const localValidation = validateProgress(local);
  const cloudValidation = validateProgress(cloud);
  if (!localValidation.ok)
    throw new Error(`invalid local progress: ${localValidation.reason}`);
  if (!cloudValidation.ok)
    throw new Error(`invalid cloud progress: ${cloudValidation.reason}`);

  const preferred = source === "local" ? local : cloud;
  const merged = cloneProgress(preferred);
  for (const key of Object.keys(merged.records) as Array<
    keyof Progress["records"]
  >) {
    merged.records[key] = Math.max(local.records[key], cloud.records[key]);
  }
  merged.currency = Math.max(local.currency, cloud.currency);
  merged.tutorial = {
    completed: local.tutorial.completed || cloud.tutorial.completed,
    step: Math.max(local.tutorial.step, cloud.tutorial.step),
  };
  if (merged.tutorial.completed) merged.tutorial.step = 5;
  merged.ownedCosmetics = { gloam: true };
  for (const id of COSMETIC_IDS) {
    if (local.ownedCosmetics[id] || cloud.ownedCosmetics[id]) {
      merged.ownedCosmetics[id] = true;
    }
  }
  merged.settings = { ...preferred.settings };
  merged.equippedCosmetic = preferred.equippedCosmetic;
  merged.revision = Math.max(local.revision, cloud.revision);
  merged.updatedAt = now;

  const validation = validateProgress(merged);
  if (!validation.ok)
    throw new Error(`merged progress is invalid: ${validation.reason}`);
  return validation.value;
};
